import React, { useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Carlos, Mascot } from "@/components/characters/Characters";
import { useLang } from "@/context/LangContext";

// One question rendered exactly like the vendor's exam-in-progress card.
export const ExamQuestionView = ({ q, lang, index = 0, total = 1, showReveal = true }) => {
  const [picked, setPicked] = useState(null);
  const opts = q.options || [];
  const correctOpt = opts.find((o) => o.id === q.correct);
  const wasCorrect = picked != null && picked === q.correct;
  return (
    <div>
      {total > 1 && (
        <div className="mb-3 flex items-center gap-3">
          <div className="font-display font-bold">{index + 1} / {total}</div>
          <div className="flex-1 h-3 bg-white border-2 border-black rounded-full overflow-hidden">
            <div className="h-full bg-canales-blue" style={{ width: `${((index + 1) / total) * 100}%` }} />
          </div>
        </div>
      )}
      <div className="brutal p-6 bg-white">
        {q.topic && (
          <div className="text-xs font-display font-bold uppercase tracking-widest text-canales-red">{q.topic}</div>
        )}
        <h3 className="mt-1 font-display font-black text-2xl">
          {q[`prompt_${lang}`] || <span className="italic text-neutral-400">{lang === "es" ? "(sin enunciado)" : "(no prompt)"}</span>}
        </h3>
        <div className="mt-4 space-y-3">
          {opts.map((o) => {
            const isPicked = picked === o.id;
            return (
              <button
                key={o.id}
                data-testid={`preview-exam-option-${o.id}`}
                onClick={() => setPicked(o.id)}
                className={`w-full text-left p-4 font-body text-base flex items-center gap-3 transition-all duration-150 ${
                  isPicked
                    ? "bg-canales-blue text-white border-[4px] border-black shadow-[6px_6px_0_#000] font-bold"
                    : "bg-white border-[3px] border-black shadow-[3px_3px_0_#000] hover:bg-canales-cream"
                }`}
              >
                <span
                  className={`flex items-center justify-center w-9 h-9 rounded-full font-display font-black text-lg shrink-0 border-2 ${
                    isPicked ? "bg-canales-yellow text-black border-black" : "bg-canales-cream text-canales-red border-canales-red"
                  }`}
                >
                  {isPicked ? <CheckCircle2 className="w-6 h-6" /> : o.id}
                </span>
                <span className="flex-1">
                  {o[lang] || <span className="italic opacity-50">{lang === "es" ? `Opción ${o.id} (vacía)` : `Option ${o.id} (empty)`}</span>}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {showReveal && picked != null && (
        <div className={`brutal p-4 mt-3 ${wasCorrect ? "bg-green-50" : "bg-red-50"}`} data-testid="preview-exam-feedback">
          <div className="flex items-start gap-3">
            <Carlos size={72} mood={wasCorrect ? "correct" : "wrong"} />
            <div className="flex-1">
              <div className={`font-display font-black text-lg ${wasCorrect ? "text-green-700" : "text-red-700"}`}>
                {wasCorrect ? (lang === "es" ? "¡Correcto!" : "Correct!") : (lang === "es" ? "Incorrecto" : "Incorrect")}
              </div>
              {!wasCorrect && correctOpt && (
                <div className="text-sm mt-1">
                  <span className="font-bold text-green-700">{lang === "es" ? "Respuesta correcta:" : "Correct answer:"} </span>
                  {correctOpt.id}. {correctOpt[lang]}
                </div>
              )}
              {(q[`explanation_${lang}`] || q[`mini_story_${lang}`]) && (
                <div className="mt-2 brutal-sm bg-white p-3">
                  {q[`explanation_${lang}`] && <p className="text-sm">{q[`explanation_${lang}`]}</p>}
                  {q[`mini_story_${lang}`] && (
                    <p className="text-sm italic mt-1 text-neutral-700">&ldquo;{q[`mini_story_${lang}`]}&rdquo;</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Single-question preview (Banco de Preguntas) — reflects the unsaved form.
export const QuestionPreview = ({ form, lang }) => (
  <div className="p-4 sm:p-6 bg-halftone">
    <div className="max-w-3xl mx-auto">
      <ExamQuestionView q={{ ...form, question_id: "preview" }} lang={lang} index={0} total={1} />
    </div>
  </div>
);

// Whole-exam preview (Exámenes) — pre-start meta + question stepper.
export const ExamPreview = ({ titleEs, titleEn, questions = [], passingScore, timeLimit, lang }) => {
  const [started, setStarted] = useState(false);
  const [cur, setCur] = useState(0);
  const total = questions.length;
  const title = lang === "es" ? titleEs : titleEn;

  if (!started) {
    return (
      <div className="p-6 bg-halftone">
        <div className="brutal p-8 bg-white text-center max-w-xl mx-auto">
          <div className="flex justify-center"><Mascot size={100} /></div>
          <h1 className="mt-4 font-display font-black text-3xl">
            {title || <span className="italic text-neutral-400">{lang === "es" ? "(sin título)" : "(untitled)"}</span>}
          </h1>
          <p className="mt-2 font-body">
            {total} {lang === "es" ? "preguntas" : "questions"}
            {timeLimit ? ` · ${Math.round(timeLimit / 60)} min` : ` · ${lang === "es" ? "sin límite" : "no limit"}`}
            {passingScore != null ? ` · ${lang === "es" ? "aprobar" : "pass"} ${passingScore}%` : ""}
          </p>
          <button
            onClick={() => { setCur(0); setStarted(true); }}
            disabled={total === 0}
            data-testid="preview-exam-start"
            className="mt-6 brutal-btn bg-canales-red text-white px-6 py-3 text-lg disabled:opacity-40"
          >
            {total === 0 ? (lang === "es" ? "Agrega preguntas primero" : "Add questions first") : (lang === "es" ? "Empezar examen" : "Start exam")}
          </button>
        </div>
      </div>
    );
  }

  const q = questions[cur];
  return (
    <div className="p-4 sm:p-6 bg-halftone">
      <div className="max-w-3xl mx-auto">
        <ExamQuestionView key={cur} q={q} lang={lang} index={cur} total={total} />
        <div className="mt-6 flex justify-between">
          <button
            onClick={() => setCur((c) => Math.max(0, c - 1))}
            disabled={cur === 0}
            className="brutal-btn bg-white px-4 py-2 disabled:opacity-40"
          >
            {lang === "es" ? "Anterior" : "Previous"}
          </button>
          {cur < total - 1 ? (
            <button
              onClick={() => setCur((c) => c + 1)}
              data-testid="preview-exam-next"
              className="brutal-btn bg-canales-blue text-white px-4 py-2 flex items-center gap-2"
            >
              {lang === "es" ? "Siguiente" : "Next"} <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => setStarted(false)}
              data-testid="preview-exam-end"
              className="brutal-btn bg-canales-red text-white px-4 py-2"
            >
              {lang === "es" ? "Terminar vista previa" : "End preview"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
